import React, {
  CSSProperties,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import QueueHeader from '../../components/Main/QueueHeader/QueueHeader';
import QueuePanel from '../../components/Main/QueuePanel/QueuePanel';
import classes from './QueuePage.module.scss';
import { useGetQueueTickers } from '../../hooks/MonitorQueue/useGetQueueTickers';
import { isQueueScreenDarkTheme } from '../../constants/API';
import { FooterAsterixIcon } from '../../assets/svgComponents/FooterAsterixIcon';
import classNames from 'classnames';
import { ClientLicenseHasExpiredModal } from '../../components/LicenseModals/LicenseHasExpiredModal/ClientLicenseHasExpiredModal';
import { useGetQueueTerminalAppearance } from '../../hooks/MonitorQueue/useGetQueueTerminalAppearance';
import { TerminalTabloElementSettings } from '../../types/Admin/TerminalAppearanceTypes';
import { TabloSlider } from '../../components/Main/TabloSlider/TabloSlider';
import { WeatherWidget } from '../../components/Main/WeatherWidget/WeatherWidget';
import { BeltaRssWidget } from '../../components/Main/BeltaRssWidget/BeltaRssWidget';
import TabloVideoLayer from '../../components/Main/TabloVideoLayer/TabloVideoLayer';
import { SimpleTabloTicker } from '../../components/Main/LowFpsMarquee/LowFpsMarquee';
import { useTabloKioskBootstrap } from '../../hooks/MonitorQueue/useTabloKioskBootstrap';
import { useGetQueueVideoPlaylist } from '../../hooks/MonitorQueue/useGetQueueVideoPlaylist';
import { isAndroidTabloDevice } from '../../utils/QueuePage/tabloDevice';

const isAndroidTablo = isAndroidTabloDevice();

/** Contain-вписывание в родителя без CSS aspect-ratio (нет в Chrome < 88 / WebView 86). */
function useContainedBoxSize(
  enabled: boolean,
  ratioW: number,
  ratioH: number
): [React.RefObject<HTMLDivElement>, CSSProperties] {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [box, setBox] = useState({ width: 0, height: 0 });

  useEffect(() => {
    if (!enabled) return;
    const el = wrapperRef.current;
    if (!el) return;

    const update = () => {
      const pw = el.clientWidth;
      const ph = el.clientHeight;
      if (pw <= 0 || ph <= 0) return;
      const parentRatio = pw / ph;
      const targetRatio = ratioW / ratioH;
      if (parentRatio > targetRatio) {
        const height = ph;
        const width = Math.round(height * targetRatio);
        setBox({ width, height });
      } else {
        const width = pw;
        const height = Math.round(width / targetRatio);
        setBox({ width, height });
      }
    };

    update();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', update);
      return () => window.removeEventListener('resize', update);
    }
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [enabled, ratioW, ratioH]);

  const style = useMemo<CSSProperties>(() => {
    if (!enabled) return {};
    if (box.width > 0 && box.height > 0) {
      return {
        width: box.width,
        height: box.height,
        maxWidth: '100%',
        maxHeight: '100%'
      };
    }
    // До первого замера — на весь контейнер (без aspect-ratio)
    return { width: '100%', height: '100%' };
  }, [enabled, box.width, box.height]);

  return [wrapperRef, style];
}

const QueuePage = () => {
  useTabloKioskBootstrap();

  const { queueTickersResp } = useGetQueueTickers();
  const { terminalAppearanceResponse, isLoadingTerminalAppearance } =
    useGetQueueTerminalAppearance();
  const [apiVideoPlaylist, setApiVideoPlaylist] = useState<string[]>([]);
  useGetQueueVideoPlaylist(setApiVideoPlaylist);
  const [isSoundPlaying, setIsSoundPlaying] = useState(false);
  const [videoFullScreen, setVideoFullScreen] = useState(false);
  const [videoHost, setVideoHost] = useState<HTMLElement | null>(null);

  const isInitialAppearanceLoading = isLoadingTerminalAppearance && !terminalAppearanceResponse;
  const tabloSettings = terminalAppearanceResponse?.settings?.tablo ?? null;
  const videoElement = tabloSettings?.elements?.video ?? null;
  const isContentVideoEnabled = videoElement?.isEnabled ?? true;
  const hasContentVideo =
    isContentVideoEnabled &&
    ((videoElement?.videoUrls?.length ?? 0) > 0 || apiVideoPlaylist.length > 0);

  const onSoundPlayingChange = useCallback((playing: boolean) => {
    setIsSoundPlaying(playing);
  }, []);
  const onVideoFullScreenChange = useCallback((fullScreen: boolean) => {
    setVideoFullScreen(fullScreen);
  }, []);
  const onVideoHostChange = useCallback((host: HTMLElement | null) => {
    setVideoHost(host);
  }, []);

  const layoutEnabled = !!tabloSettings;
  const backgrounds = terminalAppearanceResponse?.backgrounds ?? [];
  const headerElement = tabloSettings?.elements?.header ?? null;
  const tickerElement = tabloSettings?.elements?.ticker ?? null;
  const sliderElement = tabloSettings?.elements?.slider ?? null;
  const weatherWidgetElement = tabloSettings?.elements?.weatherWidget ?? null;
  const rssBeltaWidgetElement = tabloSettings?.elements?.rssBeltaWidget ?? null;
  const isHeaderEnabled = headerElement?.isEnabled ?? true;
  const isTickerEnabled = tickerElement?.isEnabled ?? true;
  const isSliderEnabled = sliderElement?.isEnabled ?? false;
  const isWeatherWidgetEnabled = weatherWidgetElement?.isEnabled ?? false;
  const isRssBeltaWidgetEnabled = rssBeltaWidgetElement?.isEnabled ?? false;

  const resolutionW = Number(tabloSettings?.resolutionWidth) || 1920;
  const resolutionH = Number(tabloSettings?.resolutionHeight) || 1080;
  const [tabloWrapperRef, tabloResolutionStyle] = useContainedBoxSize(
    layoutEnabled,
    resolutionW,
    resolutionH
  );

  const activeBackground = useMemo(() => {
    if (!tabloSettings?.backgroundId) {
      return null;
    }
    return backgrounds.find((item) => item.id === tabloSettings.backgroundId) ?? null;
  }, [backgrounds, tabloSettings?.backgroundId]);

  const buildElementStyle = useCallback(
    (element?: TerminalTabloElementSettings | null): CSSProperties | undefined => {
      if (!layoutEnabled || !element) {
        return undefined;
      }
      return {
        position: 'absolute',
        left: `${element.x}%`,
        top: `${element.y}%`,
        width: `${element.width}%`,
        height: `${element.height}%`,
        zIndex: 1,
        backgroundColor: element.backgroundColor || undefined
      };
    },
    [layoutEnabled]
  );

  const headerStyle = useMemo(() => buildElementStyle(headerElement), [buildElementStyle, headerElement]);
  const tickerStyle = useMemo(() => buildElementStyle(tickerElement), [buildElementStyle, tickerElement]);
  const sliderStyle = useMemo(() => buildElementStyle(sliderElement), [buildElementStyle, sliderElement]);
  const weatherStyle = useMemo(
    () => buildElementStyle(weatherWidgetElement),
    [buildElementStyle, weatherWidgetElement]
  );
  const rssBeltaStyle = useMemo(
    () => buildElementStyle(rssBeltaWidgetElement),
    [buildElementStyle, rssBeltaWidgetElement]
  );

  const backgroundStyle = useMemo<CSSProperties>(() => {
    if (!layoutEnabled) {
      return {};
    }
    return {
      backgroundColor: tabloSettings?.backgroundColor || undefined
    };
  }, [layoutEnabled, tabloSettings?.backgroundColor]);

  const tickerDurationSeconds =
    terminalAppearanceResponse?.settings?.tickerSpeed &&
    Number.isFinite(terminalAppearanceResponse.settings.tickerSpeed)
      ? Number(terminalAppearanceResponse.settings.tickerSpeed)
      : 50;

  const tickerTextStyle = useMemo<CSSProperties>(() => {
    return {
      fontFamily: tickerElement?.fontFamily || undefined,
      fontWeight: tickerElement?.fontWeight || undefined,
      fontSize: tickerElement?.fontSize ? `${tickerElement.fontSize}px` : undefined,
      color: tickerElement?.textColor || undefined
    };
  }, [
    tickerElement?.fontFamily,
    tickerElement?.fontWeight,
    tickerElement?.fontSize,
    tickerElement?.textColor
  ]);

  const backgroundObjectFit = useMemo(() => {
    switch (tabloSettings?.backgroundFitMode) {
      case 'contain':
        return 'contain';
      case 'stretch':
        return 'fill';
      case 'width':
      case 'height':
        return 'contain';
      case 'none':
        return 'none';
      case 'cover':
      default:
        return 'cover';
    }
  }, [tabloSettings?.backgroundFitMode]);

  const videoLayer = (
    <TabloVideoLayer
      videoElement={videoElement}
      layoutMode={layoutEnabled}
      videoHost={videoHost}
      isPauseVideo={isSoundPlaying}
      videoFullScreen={videoFullScreen}
      videoVolumeOverride={terminalAppearanceResponse?.settings?.videoVolume ?? null}
    />
  );

  const layoutContent = (
    <>
      {layoutEnabled && activeBackground && (
        <div className={classes.layoutBackground}>
          {activeBackground.mediaType === 'VIDEO' && !hasContentVideo ? (
            <video
              className={classes.layoutBackgroundMedia}
              style={{ objectFit: backgroundObjectFit }}
              autoPlay
              muted
              loop
              playsInline
              preload="auto"
              src={activeBackground.url}
            />
          ) : activeBackground.mediaType !== 'VIDEO' ? (
            <img
              className={classes.layoutBackgroundMedia}
              style={{ objectFit: backgroundObjectFit }}
              src={activeBackground.url}
              alt=""
            />
          ) : (
            <div
              className={classes.layoutBackgroundMedia}
              style={{ backgroundColor: tabloSettings?.backgroundColor || '#000' }}
            />
          )}
        </div>
      )}
      {isHeaderEnabled && (
        <QueueHeader
          logoSettings={
            layoutEnabled && terminalAppearanceResponse?.settings?.tablo?.logo != null
              ? terminalAppearanceResponse.settings.tablo.logo
              : terminalAppearanceResponse?.settings?.logo ?? null
          }
          tabloHeader={headerElement}
          containerStyle={headerStyle}
        />
      )}
      {layoutEnabled && videoLayer}
      <QueuePanel
        tabloSettings={tabloSettings ?? undefined}
        layoutMode={layoutEnabled}
        onSoundPlayingChange={onSoundPlayingChange}
        onVideoFullScreenChange={onVideoFullScreenChange}
        onVideoHostChange={onVideoHostChange}
      />
      {!layoutEnabled && videoLayer}
      {layoutEnabled && isSliderEnabled && sliderElement && (
        <div className={classes.layoutItem} style={sliderStyle}>
          <TabloSlider element={sliderElement} />
        </div>
      )}
      {layoutEnabled && isWeatherWidgetEnabled && weatherWidgetElement && (
        <div className={classes.layoutItem} style={weatherStyle}>
          <WeatherWidget element={weatherWidgetElement} />
        </div>
      )}
      {layoutEnabled && isRssBeltaWidgetEnabled && rssBeltaWidgetElement && (
        <div className={classes.layoutItem} style={rssBeltaStyle}>
          <BeltaRssWidget element={rssBeltaWidgetElement} />
        </div>
      )}
      {isTickerEnabled && (
        <footer
          className={classNames(classes.footerContainer, {
            [classes.darkThemeFooterContainer]: isQueueScreenDarkTheme,
            [classes.layoutItem]: layoutEnabled
          })}
          style={tickerStyle}>
          {queueTickersResp && (
            <SimpleTabloTicker
              items={queueTickersResp.tickers}
              durationSeconds={tickerDurationSeconds}
              className={classes.footerMarquee}
              style={tickerTextStyle}
              textClassName={classNames(classes.footerText, {
                [classes.darkThemeFooterText]: isQueueScreenDarkTheme
              })}
              separator={<FooterAsterixIcon />}
              lightMode={isAndroidTablo}
            />
          )}
        </footer>
      )}
    </>
  );

  if (isInitialAppearanceLoading) {
    return (
      <div className={classes.queuePageContainer}>
        <ClientLicenseHasExpiredModal />
      </div>
    );
  }

  return (
    <div
      className={classNames(classes.queuePageContainer, {
        [classes.darkQueuePageContainer]: isQueueScreenDarkTheme && !layoutEnabled,
        [classes.layoutEnabled]: layoutEnabled
      })}
      style={!layoutEnabled ? backgroundStyle : undefined}>
      {layoutEnabled ? (
        <div ref={tabloWrapperRef} className={classes.tabloLayoutWrapper}>
          <div
            className={classes.tabloLayout}
            style={{ ...tabloResolutionStyle, ...backgroundStyle }}>
            {layoutContent}
          </div>
        </div>
      ) : (
        layoutContent
      )}

      <ClientLicenseHasExpiredModal />
    </div>
  );
};

export default QueuePage;
